import type { User } from '../../../@types/User';

export interface SmartAttributionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  companyUsers: User[];
  companyId: number | null;
  jwt: string | null;
  salesEntries: SmartAttributionSalesEntry[];
  totalCoprosCount: number;
  onApply: (assignments: SmartAttributionCluster[]) => Promise<void>;
}

export interface SmartAttributionCoordinate {
  lat: number;
  long: number;
}

export interface SmartAttributionTarget {
  user: User;
  percentage: number;
  anchor?: SmartAttributionCoordinate | null;
}

export interface SmartAttributionTargetInput {
  userId: number;
  percentage: number;
  anchor?: SmartAttributionCoordinate | null;
}

export interface SmartAttributionYearOption {
  value: number;
  label: string;
}

export interface SmartAttributionSalesEntry {
  id: number;
  immatriculation: string;
  name: string;
  address: string;
  city: string;
  cp: string;
  salesAmount: number;
  salesCount?: number | null;
  nbLots?: number | null;
}

export interface SmartAttributionCoproInput {
  id: number;
  name: string;
  salesAmount: number;
  salesCount?: number | null;
  address?: string;
  lat?: number | null;
  long?: number | null;
}

export interface SmartAttributionAssignment {
  coproId: number;
  coproName: string;
  salesAmount: number;
  lat: number | null;
  long: number | null;
}

export interface SmartAttributionCluster {
  userId: number;
  targetPercentage: number;
  targetSalesAmount: number;
  assignedSalesAmount: number;
  assignments: SmartAttributionAssignment[];
  coveragePercentage: number;
  anchor?: SmartAttributionCoordinate | null;
}

export interface SmartAttributionResult {
  totalSalesAmount: number;
  clusters: SmartAttributionCluster[];
}

export interface SmartAttributionParams {
  copros: SmartAttributionCoproInput[];
  targets: SmartAttributionTargetInput[];
  allowAnchorsFallback?: boolean;
  debugParams?: SmartAttributionDebugParams;
}

export interface SmartAttributionMarker {
  transactionnaireId: number;
  coproId: number;
  lat: number;
  long: number;
  salesAmount: number;
  coproName: string;
}

export interface SmartAttributionCoproMarker extends SmartAttributionCoordinate {
  coproId: number;
  coproName: string;
  salesAmount: number;
  assignedUserId: number | null;
}

export interface SmartAttributionManagerMarker extends SmartAttributionCoordinate {
  userId: number;
  userName: string;
  color: string;
  draggable?: boolean;
}

export interface SmartAttributionMapProps {
  coproMarkers: SmartAttributionCoproMarker[];
  managerMarkers: SmartAttributionManagerMarker[];
  highlightedUserId?: number | null;
  onHighlight?: (userId: number | null) => void;
  onManagerMove?: (userId: number, coordinate: SmartAttributionCoordinate) => void;
  readOnly?: boolean;
}

export interface SmartAttributionDebugParams {
  earthRadiusKm?: number;
  epsilon?: number;
  capacityTolerance?: number;
  knnK?: number;
  knnMaxDistanceKm?: number;
  knnDistanceWeight?: number;
  knnCapacityWeight?: number;
  distancePriorityDivider?: number;
  maxAssignmentPasses?: number;
  maxRebalanceIterations?: number;
  distanceReassignPenalty?: number;
  rebalanceNeighborCount?: number;
  rebalanceMaxDistanceKm?: number;
  rebalanceDistanceGainWeight?: number;
}

export const DEBUG_DEFAULTS: Required<SmartAttributionDebugParams> = {
  earthRadiusKm: 6371,
  epsilon: 1e-6,
  capacityTolerance: 0.03, // Reduced tolerance to allow more coverage gaps
  knnK: 3,
  knnMaxDistanceKm: 50,
  knnDistanceWeight: 0.95, // Increased weight for distance/proximity
  knnCapacityWeight: 0.05, // Decreased weight for capacity balance
  distancePriorityDivider: 60, // Lower divider = higher distance priority
  maxAssignmentPasses: 1000,
  maxRebalanceIterations: 1000,
  distanceReassignPenalty: 0.1, // Further reduced penalty for over-capacity moves
  rebalanceNeighborCount: 3,
  rebalanceMaxDistanceKm: Number.POSITIVE_INFINITY,
  rebalanceDistanceGainWeight: 0.5, // Increased weight for distance improvements
};

export const hasCoordinate = (coordinate?: SmartAttributionCoordinate | null): coordinate is SmartAttributionCoordinate => !!coordinate
  && Number.isFinite(coordinate.lat)
  && Number.isFinite(coordinate.long);

export const toTargetInput = (target: SmartAttributionTarget): SmartAttributionTargetInput => ({
  userId: target.user.id,
  percentage: target.percentage,
  anchor: hasCoordinate(target.anchor) ? target.anchor : undefined,
});

export const mergeAnchor = (
  input: SmartAttributionTargetInput,
  coordinate?: SmartAttributionCoordinate | null,
): SmartAttributionTargetInput => ({
  ...input,
  anchor: hasCoordinate(coordinate) ? coordinate : input.anchor,
});


